import React, { useCallback, useState } from 'react';
import {
  Button,
  Field,
  Stack,
  Textarea,
  Text,
  Select,
  Label,
  Breakpoint,
  ModalOverlay
} from '@moda/om';
import { useForm } from '@moda/portal-stanchions';
import { CONFIG } from '../../../config';

import './ContactForm.scss';

enum STATUS {
  ERROR,
  SUCCESS
}

type Props = {
  showOrderNumberInput?: boolean;
};

type ContactFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  order: string;
  comments: string;
  topic: string;
};

type Options = {
  label: string;
  value: string;
}[];

const FORM_SUBMISSION_URL = `https://modaoperandi.zendesk.com/api/v2/requests.json`;
export const FORM_SUBMISSION_SUCCESS =
  'Our Customer Care team will review your inquiry and will be in touch as soon as possible.';

const OPTIONS: Options = [
  { label: 'Order Issue', value: 'Order Issue' },
  { label: 'Billing and Payments', value: 'Billing and Payments' },
  { label: 'Technical Support', value: 'Technical Support' },
  { label: 'Product Inquiry', value: 'Product Inquiry' },
  { label: 'Feedback', value: 'Feedback' },
  { label: 'Account/Order Request', value: 'Account/Order Request' },
  { label: 'Other  ', value: 'Other' }
];

export const validations = {
  firstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  lastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  },
  email: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  comments: {
    maxLength: {
      message: 'Comment should be max 255 characters.',
      value: 255
    },
    required: {
      message: 'Please leave a comment.',
      value: true
    }
  },
  topic: {
    topic: {
      message: 'Please select a topic.',
      value: true
    },
    required: {
      message: 'Please select a topic.',
      value: true
    }
  }
};

const constructBody = ({
  firstName,
  lastName,
  order,
  email,
  phone,
  topic,
  comments
}: ContactFormValues) => {
  return `First Name: ${firstName}
   Last Name: ${lastName}
   Email address: ${email}
   Order Number: ${order}
   Phone: ${phone}
   Topic: ${topic}
   Message:

   ${comments}`;
};

const customFields = (order: string) => {
  return order
    ? [
        // Add order id to ticket
        { id: 23550905, value: order }
      ]
    : [];
};

const submitContactUsForm = async ({
  firstName,
  lastName,
  email,
  phone,
  order,
  topic,
  comments
}: ContactFormValues) => {
  const Auth = encodeURIComponent(`${email}/token:${CONFIG.ZENDESK_API_TOKEN}`);
  const response = await fetch(FORM_SUBMISSION_URL, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    redirect: 'follow',
    referrer: 'no-referrer',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${Auth}`
    },
    body: JSON.stringify({
      request: {
        requester: { name: `${firstName} ${lastName}`, email: email },
        subject: 'Request Create',
        comment: {
          body: constructBody({ firstName, lastName, order, email, phone, topic, comments })
        },
        custom_fields: customFields(order)
      }
    })
  });
  const data = await response.json();
  if (data.errors && data.errors[0]) {
    return { status: STATUS.ERROR, message: data.errors[0].detail };
  }
  return { status: STATUS.SUCCESS, message: FORM_SUBMISSION_SUCCESS };
};

export const ContactForm: React.FC<Props> = () => {
  const [result, setResult] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [show, setShow] = useState(false);

  const onSubmit = useCallback(
    (values: ContactFormValues) => {
      submitContactUsForm(values)
        .then(response => {
          if (response.status === STATUS.SUCCESS) {
            setIsSubmitted(true);
          }
          setShow(true);
          setResult(response.message);
        })
        .catch(error => setResult(error.message));
    },
    [setResult]
  );

  const { errors: fieldErrors, handleSubmit } = useForm({ onSubmit, validations });

  return (
    <form className="ContactForm" onSubmit={handleSubmit}>
      <div className="ContactForm__fields">
        <div className="ContactForm__group">
          <Field
            className="ContactForm__field"
            error={fieldErrors.firstName?.message}
            label="First Name*"
            type="text"
            name="firstName"
            maxLength={50}
          />
          <Field
            className="ContactForm__field"
            error={fieldErrors.lastName?.message}
            label="Last Name*"
            type="text"
            name="lastName"
            maxLength={50}
          />
        </div>
        <div className="ContactForm__group">
          <Field
            className="ContactForm__field"
            error={fieldErrors.email?.message}
            label="Email*"
            type="text"
            name="email"
          />
          <Field className="ContactForm__field" label="Phone" type="text" name="phone" />
        </div>
        <Breakpoint gt="sm">
          <Stack space={4} direction="horizontal">
            <Field
              className="ContactForm__field"
              label="Order Number"
              type="number"
              name="order"
              error={fieldErrors.order?.message}
            />
            <div className="ContactForm__field" />
          </Stack>
        </Breakpoint>
        <Breakpoint lt="sm">
          <Field
            className="ContactForm__field"
            label="Order Number"
            type="number"
            name="order"
            error={fieldErrors.order?.message}
          />
        </Breakpoint>
        <div className="ContactForm__section-stretch">
          <Stack className="ContactForm____field" space={1}>
            <Label>Topic*</Label>
            <Select
              name="topic"
              options={OPTIONS}
              label="Select"
              error={fieldErrors.topic?.message}
            />
          </Stack>
        </div>
        <div className="ContactForm__section">
          <Field
            className="ContactForm__field"
            error={fieldErrors.comments?.message}
            label="Comments"
            type="text"
            name="comments"
            maxLength={255}
          >
            <Textarea rows={6} />
          </Field>
        </div>
      </div>

      <ModalOverlay show={show} onClose={() => setShow(false)}>
        <div className="ContactForm__modal">
          <Stack space={5}>
            <Stack space={4}>
              <Text className="ContactForm__modal-title" treatment="h3" family="serif">
                We are happy to help
              </Text>
              <Text className="ContactForm__modal-description">{result}</Text>
            </Stack>
            <Button className="ContactForm__modal-button" onClick={() => setShow(false)}>
              Continue Shopping
            </Button>
          </Stack>
        </div>
      </ModalOverlay>

      <Stack space={4} className="ContactForm__submit">
        <Button className="ContactForm__button" disabled={isSubmitted} type="submit">
          SUBMIT
        </Button>
      </Stack>
    </form>
  );
};
